import { AlertCircle, Check, Eye, EyeOff } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ConsentChoices from "../../components/consent/ConsentChoices";
import LanguageSwitcher from "../../components/ui/LanguageSwitcher";
import type { CoreDecisions } from "../../utils/coreConsents";
import { useAuthStore } from "../../stores/authStore";
import { useI18nStore } from "../../stores/i18nStore";
import { createCheckoutSession } from "../../services/stripe";
import { rememberPlanIntent, takePlanIntent } from "../../utils/planIntent";
import { markFirstRunAfterPayment } from "../../utils/firstRunAfterPayment";
import { getAttribution } from "../../utils/attribution";

export const RegisterPage: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [consents, setConsents] = useState<CoreDecisions>({});
  // Honeypot (backend: keycloak-auth.ts /register). Hidden from people and
  // from assistive technology; only a form-filling bot puts anything here.
  const [website, setWebsite] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18nStore();
  const register = useAuthStore((state) => state.register);

  // A plan chosen on the pricing page or a plan card (?plan=plus): checkout
  // opens for it right after registration.
  useEffect(() => {
    rememberPlanIntent(location.search);
  }, [location.search]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError(t.auth2PasswordsDoNotMatch);
      setIsLoading(false);
      return;
    }

    if (formData.password.length < 8) {
      setError(t.passwordTooShort);
      setIsLoading(false);
      return;
    }

    if (!consents.TERMS_OF_SERVICE) {
      setError(t.acceptTermsRequired);
      setIsLoading(false);
      return;
    }

    try {
      await register({
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        website,
        // Where this visit came from, for partner and channel attribution.
        // Undefined under Global Privacy Control or Do Not Track.
        attribution: getAttribution(),
        // Every box is an answer; an unticked one is stored as declined.
        consents: {
          TERMS_OF_SERVICE: true,
          HEALTH_DATA: consents.HEALTH_DATA === true,
          AI_PROCESSING: consents.AI_PROCESSING === true,
          MARKETING_COMMUNICATIONS: consents.MARKETING_COMMUNICATIONS === true,
        },
      });

      const plan = takePlanIntent();
      const token = useAuthStore.getState().token;
      if (plan && token) {
        // First run follows the payment (PaymentSuccessPage), so the card is
        // built either way.
        markFirstRunAfterPayment();
        try {
          // Backing out of the confirmation lands on first run instead.
          if (await createCheckoutSession(plan, token)) return;
        } catch {
          // Checkout could not open; the account exists, so carry on with
          // first run. The plan can still be chosen from the app.
        }
      }
      navigate("/traveler/welcome");
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err.message || t.registrationFailed);
      } else {
        setError(t.registrationFailed);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "var(--ui-auth-backdrop)",
        fontFamily:
          'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        position: "relative",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Background Effects */}
      <div
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          width: "300px",
          height: "300px",
          background:
            "radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%)",
          borderRadius: "50%",
          filter: "blur(40px)",
          zIndex: 1,
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "300px",
          height: "300px",
          background:
            "radial-gradient(circle, rgba(34, 197, 94, 0.1) 0%, transparent 70%)",
          borderRadius: "50%",
          filter: "blur(40px)",
          zIndex: 1,
        }}
      />

      {/* Main Content */}
      <main
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "20px",
          position: "relative",
          zIndex: 10,
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: "420px",
            margin: "0 auto",
          }}
        >
          {/* HealthPass Logo */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginBottom: "32px",
              width: "100%",
              maxWidth: "420px",
            }}
          >
            <img
              src="/images/healthpass_logo.png"
              alt={t.auth2LogoAlt}
              style={{
                width: "100%",
                maxWidth: "320px",
                height: "auto",
              }}
            />
          </div>

          {/* Welcome Section */}
          <div
            style={{
              textAlign: "center",
              marginBottom: "32px",
            }}
          >
            <h2
              style={{
                fontSize: "28px",
                fontWeight: "bold",
                color: "var(--ui-text-strong)",
                margin: "0 0 8px 0",
                lineHeight: "1.3",
              }}
            >
              {t.createAccount}
            </h2>
            <p
              style={{
                color: "var(--ui-text-muted)",
                fontSize: "16px",
                margin: 0,
                lineHeight: "1.5",
              }}
            >
              {t.createAccountSubtitle}
            </p>
          </div>

          {/* Register Form */}
          <div
            style={{
              background: "var(--ui-glass)",
              backdropFilter: "blur(20px)",
              borderRadius: "24px",
              padding: "32px",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              position: "relative",
              marginBottom: "24px",
            }}
          >
            <form
              onSubmit={handleSubmit}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "20px",
              }}
            >
              {/* Language Selector */}
              <div style={{ marginBottom: "8px" }}>
                <LanguageSwitcher />
              </div>

              {/* Name Fields */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "12px",
                }}
              >
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "var(--ui-text-body)",
                      marginBottom: "6px",
                    }}
                  >
                    {t.firstName}*
                  </label>
                  <input
                    name="firstName"
                    type="text"
                    required
                    placeholder={t.auth2FirstNamePlaceholder}
                    value={formData.firstName}
                    onChange={handleInputChange}
                    style={{
                      width: "100%",
                      padding: "14px 16px",
                      background: "var(--ui-field)",
                      border: "1px solid var(--ui-line)",
                      borderRadius: "12px",
                      fontSize: "16px",
                      outline: "none",
                      transition: "all 0.2s",
                      boxSizing: "border-box",
                    }}
                    onFocus={(e) =>
                      (e.target.style.borderColor = "var(--ui-link)")
                    }
                    onBlur={(e) =>
                      (e.target.style.borderColor = "var(--ui-line)")
                    }
                  />
                </div>
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "var(--ui-text-body)",
                      marginBottom: "6px",
                    }}
                  >
                    {t.lastName}*
                  </label>
                  <input
                    name="lastName"
                    type="text"
                    required
                    placeholder={t.auth2LastNamePlaceholder}
                    value={formData.lastName}
                    onChange={handleInputChange}
                    style={{
                      width: "100%",
                      padding: "14px 16px",
                      background: "var(--ui-field)",
                      border: "1px solid var(--ui-line)",
                      borderRadius: "12px",
                      fontSize: "16px",
                      outline: "none",
                      transition: "all 0.2s",
                      boxSizing: "border-box",
                    }}
                    onFocus={(e) =>
                      (e.target.style.borderColor = "var(--ui-link)")
                    }
                    onBlur={(e) =>
                      (e.target.style.borderColor = "var(--ui-line)")
                    }
                  />
                </div>
              </div>

              {/* Email Field */}
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "var(--ui-text-body)",
                    marginBottom: "6px",
                  }}
                >
                  {t.email}*
                </label>
                <input
                  name="email"
                  type="email"
                  required
                  placeholder={t.auth2EmailPlaceholder}
                  value={formData.email}
                  onChange={handleInputChange}
                  style={{
                    width: "100%",
                    padding: "14px 16px",
                    background: "var(--ui-field)",
                    border: "1px solid var(--ui-line)",
                    borderRadius: "12px",
                    fontSize: "16px",
                    outline: "none",
                    transition: "all 0.2s",
                    boxSizing: "border-box",
                  }}
                  onFocus={(e) =>
                    (e.target.style.borderColor = "var(--ui-link)")
                  }
                  onBlur={(e) =>
                    (e.target.style.borderColor = "var(--ui-line)")
                  }
                />
              </div>

              {/* Password Field */}
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "var(--ui-text-body)",
                    marginBottom: "6px",
                  }}
                >
                  {t.password}*
                </label>
                <div style={{ position: "relative" }}>
                  <input
                    name="password"
                    type={showPassword ? "text" : "password"}
                    required
                    placeholder={t.auth2PasswordMinPlaceholder}
                    value={formData.password}
                    onChange={handleInputChange}
                    style={{
                      width: "100%",
                      padding: "14px 16px",
                      paddingRight: "48px",
                      background: "var(--ui-field)",
                      border: "1px solid var(--ui-line)",
                      borderRadius: "12px",
                      fontSize: "16px",
                      outline: "none",
                      transition: "all 0.2s",
                      boxSizing: "border-box",
                    }}
                    onFocus={(e) =>
                      (e.target.style.borderColor = "var(--ui-link)")
                    }
                    onBlur={(e) =>
                      (e.target.style.borderColor = "var(--ui-line)")
                    }
                  />
                  <button
                    type="button"
                    style={{
                      position: "absolute",
                      right: "14px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "none",
                      border: "none",
                      color: "#9ca3af",
                      cursor: "pointer",
                      padding: "4px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={
                      showPassword ? t.auth2HidePassword : t.auth2ShowPassword
                    }
                  >
                    {showPassword ? (
                      <EyeOff style={{ width: "20px", height: "20px" }} />
                    ) : (
                      <Eye style={{ width: "20px", height: "20px" }} />
                    )}
                  </button>
                </div>
              </div>

              {/* Confirm Password Field */}
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "var(--ui-text-body)",
                    marginBottom: "6px",
                  }}
                >
                  {t.confirmPassword}*
                </label>
                <div style={{ position: "relative" }}>
                  <input
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    required
                    placeholder={t.auth2RepeatPasswordPlaceholder}
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    style={{
                      width: "100%",
                      padding: "14px 16px",
                      paddingRight: "48px",
                      background: "var(--ui-field)",
                      border: "1px solid var(--ui-line)",
                      borderRadius: "12px",
                      fontSize: "16px",
                      outline: "none",
                      transition: "all 0.2s",
                      boxSizing: "border-box",
                    }}
                    onFocus={(e) =>
                      (e.target.style.borderColor = "var(--ui-link)")
                    }
                    onBlur={(e) =>
                      (e.target.style.borderColor = "var(--ui-line)")
                    }
                  />
                  <button
                    type="button"
                    style={{
                      position: "absolute",
                      right: "14px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "none",
                      border: "none",
                      color: "#9ca3af",
                      cursor: "pointer",
                      padding: "4px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    aria-label={
                      showConfirmPassword
                        ? t.auth2HidePassword
                        : t.auth2ShowPassword
                    }
                  >
                    {showConfirmPassword ? (
                      <EyeOff style={{ width: "20px", height: "20px" }} />
                    ) : (
                      <Eye style={{ width: "20px", height: "20px" }} />
                    )}
                  </button>
                </div>
              </div>

              <div
                aria-hidden="true"
                style={{
                  position: "absolute",
                  left: "-10000px",
                  width: "1px",
                  height: "1px",
                  overflow: "hidden",
                }}
              >
                <label htmlFor="website">Website</label>
                <input
                  id="website"
                  name="website"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                />
              </div>

              {/* The sign-up consents, each its own box (ConsentChoices) */}
              <ConsentChoices value={consents} onChange={setConsents} />

              {/* Error Message */}
              {error && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    color: "var(--ui-error-fg)",
                    background: "var(--ui-error-bg)",
                    padding: "12px 16px",
                    borderRadius: "12px",
                    border: "1px solid var(--ui-error-line)",
                  }}
                >
                  <AlertCircle
                    style={{ width: "20px", height: "20px", flexShrink: 0 }}
                  />
                  <span style={{ fontSize: "14px", fontWeight: "500" }}>
                    {error}
                  </span>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                style={{
                  width: "100%",
                  height: "50px",
                  background: "linear-gradient(45deg, #3b82f6, #22c55e)",
                  color: "white",
                  border: "none",
                  borderRadius: "12px",
                  fontSize: "16px",
                  fontWeight: "600",
                  cursor: isLoading ? "not-allowed" : "pointer",
                  boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
                  transition: "all 0.2s",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  opacity: isLoading ? 0.8 : 1,
                }}
              >
                {isLoading ? (
                  <>
                    <div
                      style={{
                        width: "20px",
                        height: "20px",
                        border: "2px solid rgba(255,255,255,0.3)",
                        borderTop: "2px solid white",
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite",
                      }}
                    />
                    <span>{t.loading}</span>
                  </>
                ) : (
                  <>
                    <Check style={{ width: "20px", height: "20px" }} />
                    <span>{t.signUp}</span>
                  </>
                )}
              </button>
            </form>

            {/* Login Link */}
            <div
              style={{
                marginTop: "24px",
                textAlign: "center",
                paddingTop: "20px",
                borderTop: "1px solid var(--ui-line-soft)",
              }}
            >
              <p
                style={{
                  fontSize: "14px",
                  color: "var(--ui-text-muted)",
                  margin: "0 0 12px 0",
                }}
              >
                {t.alreadyHaveAccount}{" "}
                <a
                  href={`/traveler/login${location.search}`}
                  style={{
                    color: "var(--ui-link)",
                    textDecoration: "none",
                    fontWeight: "500",
                  }}
                >
                  {t.signIn}
                </a>
              </p>
            </div>
          </div>

          {/* Footer */}
          <div
            style={{
              textAlign: "center",
              fontSize: "14px",
              color: "var(--ui-text-muted)",
            }}
          >
            <p style={{ margin: "0 0 8px 0" }}>
              © {new Date().getFullYear()} HealthPass
            </p>
            <div
              style={{
                marginTop: "12px",
                display: "flex",
                justifyContent: "center",
                gap: "16px",
                fontSize: "12px",
              }}
            >
              <a
                href="/privacy"
                style={{ color: "var(--ui-link)", textDecoration: "none" }}
              >
                {t.privacyPolicy}
              </a>
              <a
                href="/terms"
                style={{ color: "var(--ui-link)", textDecoration: "none" }}
              >
                {t.termsAndConditions}
              </a>
              <a
                href="/imprint"
                style={{ color: "var(--ui-link)", textDecoration: "none" }}
              >
                {t.auth2Imprint}
              </a>
            </div>
          </div>
        </div>
      </main>

      {/* CSS animation for the spinner */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};
